import React from 'react';
import { Box, Chip } from '@mui/material';
import { abyssColors } from '@/theme/colors';
import type { FilterPill } from './InsightsSearchBar.types';

interface FilterPillsProps {
  pills: FilterPill[];
  onRemovePill: (pill: FilterPill) => void;
  onEditPill?: (pill: FilterPill) => void;
  editingPill?: FilterPill | null;
  editingValue?: string;
  onChangeEditingValue?: (value: string) => void;
  onSaveEditedPill?: (pill: FilterPill, newValue: string) => void;
  onCancelEdit?: () => void;
}

const isPillBeingEdited = (pill: FilterPill, editingPill: FilterPill | null | undefined) =>
  editingPill &&
  editingPill.fieldKey === pill.fieldKey &&
  editingPill.value === pill.value &&
  editingPill.type === pill.type;

export const FilterPills = ({
  pills,
  onRemovePill,
  onEditPill,
  editingPill,
  editingValue,
  onChangeEditingValue,
  onSaveEditedPill,
  onCancelEdit,
}: FilterPillsProps) => {
  return (
    <>
      {pills.map((pill, index) => {
        const key = `${pill.fieldKey}-${pill.value}-${index}`;

        if (isPillBeingEdited(pill, editingPill)) {
          return (
            <Box
              key={key}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                backgroundColor: abyssColors.primary[100],
                color: abyssColors.primary[600],
                borderRadius: '0.25rem',
                px: '0.5rem',
                minHeight: '1.8rem',
                mr: '0.25rem',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                value={editingValue ?? ''}
                onChange={(e) => onChangeEditingValue?.(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    onSaveEditedPill?.(pill, (editingValue ?? '').trim());
                  }
                }}
                onBlur={() => onCancelEdit?.()}
                autoFocus
                aria-label={`Edit ${pill.fieldName} pill`}
                style={{
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  fontSize: '1rem',
                  color: abyssColors.primary[600],
                  width: `${Math.max((editingValue ?? '').length, 6)}ch`,
                  maxWidth: 'min(80vw, 60rem)',
                  whiteSpace: 'nowrap',
                }}
              />
            </Box>
          );
        }

        return (
          <Chip
            key={key}
            label={`${pill.fieldName}: ${pill.displayValue}`}
            size="small"
            onDelete={() => onRemovePill(pill)}
            onClick={(e) => {
              e.stopPropagation();
              onEditPill?.(pill);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onEditPill?.(pill);
              }
            }}
            tabIndex={0}
            role="button"
            sx={{
              backgroundColor: abyssColors.primary[100],
              color: abyssColors.primary[600],
              '& .MuiChip-deleteIcon': { color: abyssColors.primary[600] },
              cursor: 'pointer',
              mr: '0.25rem',
            }}
          />
        );
      })}
    </>
  );
};
